<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <!-- invisible view to orchestrate photo capture, confirmation,
  direction selection, distance and weight input, and visualization -->
  <div>
    <CameraCapture
      v-if="showCamera"
      @close="handleCameraClose"
      @photo="handlePhoto" />

    <!-- Photo Confirmation -->
    <PhotoConfirmation
      v-if="showConfirmation"
      :photo-data="capturedPhoto"
      @retake="handleRetake"
      @accept="handleAcceptPhoto"
      @close="cancel" />

    <!-- Direction Selection -->
    <DirectionPicker
      v-model:show="showDirectionDialog"
      :title="$t('views.segmentation.directionChoice.label')"
      :subtitle="$t('views.segmentation.directionChoice.subtitle')"
      @select="handleDirectionSelected"
      @cancel="cancel" />

    <!-- Distance Input -->
    <ValueInput
      :show="showDistanceDialog"
      :title="$t('views.segmentation.distanceChoice.label')"
      :subtitle="$t('views.segmentation.distanceChoice.subtitle')"
      :placeholder="$t('views.segmentation.distanceChoice.placeholder')"
      :inputMode="'decimal'"
      :validation="{ type: 'range', min: 0.1, max: 10, unit: 'm' }"
      :save-label="$t('common.buttons.continue')"
      @save="handleDistanceSaved"
      @cancel="cancel" />

    <!-- Weight Input for saving -->
    <ValueInput
      :show="showWeightDialog"
      :title="$t('views.session.weight.label')"
      :placeholder="$t('views.session.weight.placeholder')"
      :inputMode="'decimal'"
      :initial-value="prefilledWeight"
      :validation="{ type: 'range', min: 1, max: 200, unit: 'kg' }"
      :save-label="$t('common.buttons.save')"
      :show-date-input="true"
      :goat-id="goat?.id"
      @save="handleWeightSaved"
      @cancel="handleWeightCancel" />

    <!-- Analyzing Toast -->
    <Toast
      :show="showAnalyzing"
      :message="
        pipelineProgress?.messageKey
          ? $t(pipelineProgress.messageKey)
          : $t('views.camera.analyzing')
      "
      :subtitle="loadingSubtitle"
      :show-subtitle="true"
      type="working"
      :auto-hide="false" />

    <!-- Orientation Initializing Toast -->
    <Toast
      :show="isOrientationInitializing"
      :message="$t('views.camera.initializingSensor')"
      type="working"
      :auto-hide="false" />

    <!-- Error Toast -->
    <Toast
      :show="showError"
      :message="errorMessage"
      :subtitle="errorSubtitle"
      :show-subtitle="!!errorSubtitle"
      type="error"
      :auto-hide="true"
      :duration="6000" />

    <!-- Calibration Info First Time Popup -->
    <CalibrationInfoModal
      :show="showCalibrationDialog"
      @update:show="showCalibrationDialog = $event"
      @calibrate="handleCalibrate"
      @skip="handleSkipCalibration" />

    <!-- Segmentation Visualization -->
    <SegmentationVisualization
      :show="showVisualization"
      :photo-data="capturedPhoto"
      :segmentation="visualizationData?.segmentation"
      :measurements="visualizationData?.calibrated"
      :metrics="visualizationData?.metrics"
      :weight="visualizationData?.weight"
      :accuracy="visualizationData?.accuracy"
      :debug-info="visualizationData?.debug"
      @close="handleVisualizationClose"
      @next="handleVisualizationNext"
      @retry="handleVisualizationRetry"
      @recalculate="handleRecalculate" />
  </div>
</template>

<script setup>
  import { ref, computed, onUnmounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useRouter } from 'vue-router'
  import { usePhotoWeightError } from '@/utils/ml/usePhotoWeightError.js'
  import CameraCapture from '@/components/camera/CameraCapture.vue'
  import PhotoConfirmation from '@/components/camera/PhotoConfirmation.vue'
  import DirectionPicker from '@/components/camera/ml/DirectionPicker.vue'
  import ValueInput from '@/components/ui/inputs/ValueInput.vue'
  import Toast from '@/components/ui/feedback/Toast.vue'
  import SegmentationVisualization from '@/components/camera/ml/SegmentationVisualization.vue'
  import CalibrationInfoModal from '@/components/camera/ml/CalibrationInfoModal.vue'
  import { getWeightPipeline } from '@/services/ml/WeightPipeline.js'
  import { useDeviceOrientation } from '@/utils/ml/useDeviceOrientation.js'
  import { ExifService } from '@/services/ml/ExifService.js'

  // Props
  const props = defineProps({
    goat: {
      type: Object,
      default: null,
    },
  })

  // Emits
  const emit = defineEmits(['result', 'error'])

  const { t } = useI18n()
  const router = useRouter()
  const { getErrorNotification } = usePhotoWeightError()

  // State
  const showCamera = ref(false)
  const showConfirmation = ref(false)
  const showDirectionDialog = ref(false)
  const showDistanceDialog = ref(false)
  const showWeightDialog = ref(false)
  const showAnalyzing = ref(false)
  const showVisualization = ref(false)
  const showCalibrationDialog = ref(false)
  const prefilledWeight = ref(null)
  const capturedPhoto = ref(null)
  const capturedOriginalBlob = ref(null)
  const capturedMlBlob = ref(null)
  const isMlBlobNormalized = ref(false)
  const metadata = ref(null)
  const pipelineProgress = ref(null)
  const selectedDirection = ref('left')
  const visualizationData = ref(null)
  const pendingResult = ref(null)
  const distance = ref(null)
  const showError = ref(false)
  const errorMessage = ref('')
  const errorSubtitle = ref('')

  // ML Pipeline
  const weightPipeline = getWeightPipeline()

  const exifService = new ExifService()

  function convertBase64ToBlob(base64) {
    try {
      const base64Data = base64.includes(',') ? base64.split(',')[1] : base64
      const byteCharacters = atob(base64Data)
      const byteNumbers = new Array(byteCharacters.length)

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }

      const byteArray = new Uint8Array(byteNumbers)
      capturedMlBlob.value = new Blob([byteArray], { type: 'image/jpeg' })
    } catch (error) {
      console.error('Failed to convert base64 to blob:', error)
      capturedMlBlob.value = null
    }
  }

  async function blobToImage(blob) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(img.src)
        resolve(img)
      }
      img.onerror = reject
      img.src = URL.createObjectURL(blob)
    })
  }

  function canvasToJpegBlob(canvas, quality = 0.92) {
    return new Promise((resolve, reject) => {
      try {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('toBlob returned null'))
              return
            }
            resolve(blob)
          },
          'image/jpeg',
          quality
        )
      } catch (e) {
        reject(e)
      }
    })
  }

  /**
   * Draw image to canvas with EXIF orientation applied.
   *
   * Modern browsers (iOS Safari 13.4+, Chrome 81+, Firefox 77+) auto-apply
   * EXIF orientation when decoding images. We detect this by comparing
   * actual image dimensions to what the raw EXIF dimensions would be
   * after the orientation transform.
   *
   * @param {HTMLImageElement} img - Decoded image element
   * @param {Object} exifData - EXIF data with orientation, rawWidth, rawHeight
   * @returns {HTMLCanvasElement} Canvas with correctly oriented image
   */
  function drawOrientedToCanvas(img, exifData) {
    const rawW = exifData?.rawWidth
    const rawH = exifData?.rawHeight
    const orientation = exifData?.orientation || 1

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    // If no orientation transform needed, just copy to canvas
    if (!orientation || orientation <= 1) {
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      ctx.drawImage(img, 0, 0)
      return canvas
    }

    // Determine if browser already applied EXIF orientation
    // Modern browsers auto-orient, so the decoded image dimensions
    // will already reflect the correct orientation
    let browserAutoOriented = false

    if (rawW && rawH) {
      // For orientations 5-8, width/height are swapped in the final image
      const needsSwap = orientation > 4

      // Expected dimensions AFTER orientation is applied
      const expectedW = needsSwap ? rawH : rawW
      const expectedH = needsSwap ? rawW : rawH

      // If actual dimensions match expected post-orientation dimensions,
      // the browser already applied the orientation
      if (img.naturalWidth === expectedW && img.naturalHeight === expectedH) {
        browserAutoOriented = true
      }
    } else {
      // No raw dimensions available
      // Assume modern browser behavior (browsers since 2020 auto-orient)
      browserAutoOriented = true
    }

    // If browser already handled orientation, just copy as-is
    if (browserAutoOriented) {
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      ctx.drawImage(img, 0, 0)
      return canvas
    }

    // Manual orientation for older browsers that don't auto-orient
    const needsSwap = orientation > 4
    canvas.width = needsSwap ? img.naturalHeight : img.naturalWidth
    canvas.height = needsSwap ? img.naturalWidth : img.naturalHeight

    switch (orientation) {
      case 2:
        ctx.transform(-1, 0, 0, 1, canvas.width, 0)
        break
      case 3:
        ctx.transform(-1, 0, 0, -1, canvas.width, canvas.height)
        break
      case 4:
        ctx.transform(1, 0, 0, -1, 0, canvas.height)
        break
      case 5:
        ctx.transform(0, 1, 1, 0, 0, 0)
        break
      case 6:
        ctx.transform(0, 1, -1, 0, canvas.width, 0)
        break
      case 7:
        ctx.transform(0, -1, -1, 0, canvas.width, canvas.height)
        break
      case 8:
        ctx.transform(0, -1, 1, 0, 0, canvas.height)
        break
    }

    ctx.drawImage(img, 0, 0)
    return canvas
  }

  async function ensureMlBlob() {
    if (capturedMlBlob.value) return
    if (!capturedOriginalBlob.value) return

    try {
      const exifData = await exifService.extractExifData(
        capturedOriginalBlob.value
      )
      const img = await blobToImage(capturedOriginalBlob.value)
      const orientedCanvas = drawOrientedToCanvas(img, exifData)
      capturedMlBlob.value = await canvasToJpegBlob(orientedCanvas, 0.92)
      isMlBlobNormalized.value = true
    } catch (e) {
      console.warn(
        '[PhotoWeight] Failed to normalize ML blob, falling back to original:',
        e?.message || e
      )
      capturedMlBlob.value = capturedOriginalBlob.value
      isMlBlobNormalized.value = false
    }
  }

  // Computed subtitle for loading toast
  const loadingSubtitle = computed(() => {
    if (
      !pipelineProgress.value ||
      pipelineProgress.value.stage === 'complete'
    ) {
      return ''
    }

    // Only show note during model initialization (not during actual processing)
    const modelLoadingKeys = [
      'ml.pipeline.loadingSegmentationModel',
      'ml.pipeline.initializingWeightEstimator',
    ]
    if (!modelLoadingKeys.includes(pipelineProgress.value.messageKey)) {
      return ''
    }

    return t('ml.loadHint')
  })

  // Device Orientation (for angle tracking)
  // only tracks when camera is active
  const {
    angle: deviceAngle,
    hasSupport: hasOrientationSupport,
    isInitializing: isOrientationInitializing,
    startTracking: startOrientationTracking,
    stopTracking: stopOrientationTracking,
  } = useDeviceOrientation(20)

  // ML pipeline will be initialized lazily on first use (when photo is taken)
  // This prevents UI lag when selecting a goat

  /**
   * Open camera to take photo
   */
  async function open() {
    // Check if first time using camera for weight
    const hasShownCalibration = localStorage.getItem(
      'hasShownCameraCalibration'
    )
    if (!hasShownCalibration) {
      showCalibrationDialog.value = true
      return
    }

    // Start orientation tracking when camera opens
    await startOrientationTracking()
    showCamera.value = true
  }

  /**
   * Handle camera close (user cancelled without taking photo)
   */
  function handleCameraClose() {
    showCamera.value = false
    // Stop orientation tracking when camera is closed
    stopOrientationTracking()
  }

  /**
   * Handle photo capture from camera
   */
  function handlePhoto({ photoData, metadata: m }) {
    capturedPhoto.value = photoData
    metadata.value = m

    capturedOriginalBlob.value = null
    capturedMlBlob.value = null
    isMlBlobNormalized.value = false

    // Prefer original capture for EXIF; ML blob will be generated (orientation baked).
    if (m?.originalFile) {
      capturedOriginalBlob.value = m.originalFile
      console.info('[PhotoWeight] Using original file for EXIF')
    } else if (m?.originalBlob) {
      capturedOriginalBlob.value = m.originalBlob
      console.info('[PhotoWeight] Using ImageCapture blob for EXIF')
    } else {
      // No EXIF available; ML will run on the square preview blob.
      convertBase64ToBlob(photoData)
      capturedOriginalBlob.value = capturedMlBlob.value
      console.info('[PhotoWeight] Using base64 blob (no EXIF available)')
    }

    showCamera.value = false
    showConfirmation.value = true
  }

  /**
   * Handle retake photo request
   */
  function handleRetake() {
    showConfirmation.value = false
    capturedPhoto.value = null
    capturedOriginalBlob.value = null
    capturedMlBlob.value = null
    metadata.value = null
    showCamera.value = true
  }

  /**
   * Handle photo acceptance
   */
  function handleAcceptPhoto() {
    showConfirmation.value = false
    showDirectionDialog.value = true
  }

  /**
   * Handle direction selection from DirectionPicker
   */
  function handleDirectionSelected(direction) {
    selectedDirection.value = direction
    showDirectionDialog.value = false
    showDistanceDialog.value = true
  }

  /**
   * Handle calibration setup
   */
  function handleCalibrate(dontShowAgain) {
    if (dontShowAgain) {
      localStorage.setItem('hasShownCameraCalibration', 'true')
    }
    showCalibrationDialog.value = false
    router.push('/more/focal-length')
  }

  /**
   * Handle skip calibration
   */
  function handleSkipCalibration(dontShowAgain) {
    if (dontShowAgain) {
      localStorage.setItem('hasShownCameraCalibration', 'true')
    }
    showCalibrationDialog.value = false
    // Proceed to open camera
    openCamera()
  }

  /**
   * Open camera after calibration check
   */
  async function openCamera() {
    await startOrientationTracking()
    showCamera.value = true
  }

  /**
   * Cancel the entire process
   */
  function cancel() {
    showDirectionDialog.value = false
    showDistanceDialog.value = false
    showConfirmation.value = false
    showCamera.value = false
    showAnalyzing.value = false
    pipelineProgress.value = null
    stopOrientationTracking()

    capturedOriginalBlob.value = null
    capturedMlBlob.value = null
  }

  onUnmounted(() => {})

  /**
   * Handle distance input saved
   */
  async function handleDistanceSaved({ processedValue }) {
    distance.value = processedValue
    showDistanceDialog.value = false
    showAnalyzing.value = true
    await processPhoto(processedValue)
    // Stop orientation tracking after photo is processed
    stopOrientationTracking()
  }

  /**
   * Process photo through ML pipeline to estimate weight
   */
  async function processPhoto(distance) {
    try {
      if (!capturedOriginalBlob.value) {
        throw new Error('No photo available')
      }

      // Ensure ML blob is available and normalized (full-frame, orientation baked)
      await ensureMlBlob()

      if (!capturedMlBlob.value) {
        throw new Error('No photo available')
      }

      // Log input parameters
      console.group('[ML] Pipeline Input')
      console.info('Distance:', distance, 'm')
      console.info(
        'Viewing Angle:',
        deviceAngle.value,
        '°',
        hasOrientationSupport.value ? '(sensor)' : '(default)'
      )
      console.info('Direction:', selectedDirection.value)
      console.groupEnd()

      // Initialize pipeline if not already done (lazy loading to prevent UI lag)
      if (!weightPipeline.isInitialized) {
        console.group('[ML] Pipeline Initialization')
        await weightPipeline.initialize((progress) => {
          console.info(
            `${progress.stage}: ${(progress.progress * 100).toFixed(0)}%`
          )
          pipelineProgress.value = progress
        })
        console.info('[ML] Pipeline ready')
        console.groupEnd()
      }

      // Run ML pipeline
      const result = await weightPipeline.estimateWeight(
        {
          image: capturedMlBlob.value,
          exifSource: capturedOriginalBlob.value,
          segmentationExifData: isMlBlobNormalized.value
            ? { orientation: 1 }
            : undefined,
          distance: distance,
          viewingAngle: deviceAngle.value, // Use actual device angle
          direction: selectedDirection.value,
          metadata: metadata.value, // Pass metadata (focal length, etc.)
        },
        (progress) => {
          pipelineProgress.value = progress
        }
      )

      // Log all measurements to console
      console.group('[ML] Weight Estimation Results')
      console.info('Weight:', result.weight, 'kg')
      console.info('[ML] Weight Estimation Results')
      console.info('Accuracy:', (result.accuracy * 100).toFixed(1) + '%')

      console.groupCollapsed('Body Measurements (pixels)')
      console.info('Length:', result.pixelMeasurements?.bodyLength)
      console.info('Height:', result.pixelMeasurements?.bodyHeight)
      console.info('Shoulder:', result.pixelMeasurements?.shoulderHeight)
      console.info('Rump:', result.pixelMeasurements?.rumpHeight)
      console.groupEnd()

      console.groupCollapsed('Calibrated (cm)')
      console.info('Length:', result.measurements?.bodyLength, 'cm')
      console.info('Height:', result.measurements?.bodyHeight, 'cm')
      console.info('Shoulder:', result.measurements?.shoulderHeight, 'cm')
      console.info('Rump:', result.measurements?.rumpHeight, 'cm')
      console.groupEnd()

      console.groupCollapsed('Segmentation')
      console.info('Confidence:', result.segmentation?.confidence?.toFixed(3))
      console.info('Bounding Box:', result.segmentation?.boundingBox)
      console.groupEnd()

      console.groupCollapsed('Debug Info')
      console.info(
        'Processing Time:',
        result.debug?.processingTimeMs?.toFixed(2),
        'ms'
      )
      console.info('Direction:', result.debug?.direction)
      console.info('Distance:', result.debug?.distance, 'm')
      console.info('Focal Length:', result.debug?.exifData?.focalLength, 'mm')
      console.info('Has EXIF:', result.debug?.exifData?.hasFocalLength)
      console.info(
        'Device:',
        result.debug?.exifData?.make,
        result.debug?.exifData?.model
      )
      console.groupEnd()

      console.groupEnd()

      showAnalyzing.value = false

      // Check for invalid weight results
      if (result.weight <= 0 || isNaN(result.weight)) {
        const debugInfo = result.debug || {}
        let errorType = 'invalid-weight'

        // Provide specific error types based on the issue
        if (!debugInfo.focalLengthUsed || debugInfo.focalLengthUsed === null) {
          errorType = 'missing-focal-length'
        } else if (debugInfo.distance > 3) {
          errorType = 'distance-too-far'
        }

        // Get display-ready error from composable
        const errorNotification = getErrorNotification({ type: errorType })

        // Emit error with ready-to-display message
        emit('error', {
          ...errorNotification,
          debug: debugInfo,
        })
        return
      }

      // Store visualization data and pending result
      visualizationData.value = {
        segmentation: result.segmentation,
        calibrated: result.measurements, // cm measurements
        metrics: result.pixelMeasurements, // pixel measurements
        weight: result.weight,
        accuracy: result.accuracy,
        debug: result.debug,
      }
      pendingResult.value = result

      // Show visualization and wait for user action
      showVisualization.value = true
    } catch (error) {
      console.error('Weight estimation failed:', error)
      showAnalyzing.value = false

      // Determine error type from error message
      let errorType = 'generic'
      const errorMsg = error.message?.toLowerCase() || ''

      if (
        errorMsg.includes('no goat detected') ||
        errorMsg.includes('segmentation failed')
      ) {
        errorType = 'no-goat'
      } else if (errorMsg.includes('focal length')) {
        errorType = 'missing-focal-length'
      } else if (
        errorMsg.includes('model') ||
        errorMsg.includes('initialization')
      ) {
        errorType = 'model-load-failed'
      } else if (errorMsg.includes('measurement')) {
        errorType = 'measurement-failed'
      }

      // Get display-ready error from composable
      const errorNotification = getErrorNotification({
        type: errorType,
        message: errorType === 'generic' ? error.message : undefined,
      })

      // Emit error with ready-to-display message
      emit('error', {
        ...errorNotification,
        originalError: error,
      })
    }
  }

  function handleVisualizationClose() {
    showVisualization.value = false
    visualizationData.value = null
    pendingResult.value = null
    // Reset camera state
    showCamera.value = false
    capturedPhoto.value = null
  }

  function handleVisualizationRetry() {
    showVisualization.value = false
    visualizationData.value = null
    pendingResult.value = null
    // Go back to camera
    showCamera.value = true
  }

  function handleVisualizationNext({ weight }) {
    const result = pendingResult.value
    if (!result) return

    // Close visualization and open weight dialog with prefilled value
    showVisualization.value = false
    prefilledWeight.value = Math.round(weight * 10) / 10
    showWeightDialog.value = true

    // Check for missing focal length
    if (!result.debug?.exifData?.focalLength) {
      console.warn('[ML] No focal length data available - using default')
      emit('error', {
        type: 'missing-focal-length',
        message:
          'No focal length found in photo EXIF data. Using default value. Results may be less accurate.',
        result: result,
      })
    }
  }

  function handleWeightSaved({ processedValue, date }) {
    const result = pendingResult.value
    if (!result) return

    showWeightDialog.value = false

    // Emit result to parent with weight and selected date
    emit('result', {
      weight: processedValue,
      distance: distance.value,
      photo: capturedPhoto.value,
      metadata: metadata.value,
      accuracy: result.accuracy,
      pipelineResult: result,
      date: date,
    })

    // Clean up
    visualizationData.value = null
    pendingResult.value = null
    prefilledWeight.value = null
  }

  function handleWeightCancel() {
    showWeightDialog.value = false
    // Go back to visualization
    showVisualization.value = true
  }

  /**
   * Handle recalculate with new parameters
   */
  async function handleRecalculate(params) {
    try {
      if (!capturedOriginalBlob.value) return

      await ensureMlBlob()

      if (!capturedMlBlob.value) return

      console.group('[ML] Recalculating with new parameters')
      console.log('Direction:', params.direction)
      console.log('Distance:', params.distance, 'm')
      console.log('Focal Length:', params.focalLength || 'default', 'mm')
      console.groupEnd()

      // Update stored values
      selectedDirection.value = params.direction
      distance.value = params.distance

      // Show analyzing state
      showVisualization.value = false
      showAnalyzing.value = true

      // Initialize pipeline if not already done (lazy loading)
      if (!weightPipeline.isInitialized) {
        console.group('[ML] Pipeline Initialization')
        await weightPipeline.initialize((progress) => {
          console.log(
            `${progress.stage}: ${(progress.progress * 100).toFixed(0)}%`
          )
          pipelineProgress.value = progress
        })
        console.log('[DONE] Pipeline ready')
        console.groupEnd()
      }

      // Re-run pipeline with new parameters
      const result = await weightPipeline.estimateWeight(
        {
          image: capturedMlBlob.value,
          exifSource: capturedOriginalBlob.value,
          segmentationExifData: isMlBlobNormalized.value
            ? { orientation: 1 }
            : undefined,
          distance: params.distance,
          viewingAngle: deviceAngle.value,
          direction: params.direction,
          focalLength: params.focalLength, // Override focal length if provided
          focal35mm: params.focal35mm, // Override 35mm equivalent if provided
        },
        (progress) => {
          pipelineProgress.value = progress
        }
      )

      // Update visualization data (same shape as initial run)
      visualizationData.value = {
        segmentation: result.segmentation,
        calibrated: result.measurements, // cm measurements
        metrics: result.pixelMeasurements, // pixel measurements
        weight: result.weight,
        accuracy: result.accuracy,
        debug: result.debug,
      }
      pendingResult.value = result

      showAnalyzing.value = false
      showVisualization.value = true
    } catch (error) {
      console.error('[ML] Recalculation failed:', error)
      showAnalyzing.value = false
      emit('error', { type: 'pipeline', message: error.message })
    }
  }

  // Expose methods for parent component
  defineExpose({ open })
</script>
